// gsiLoader loads (or reuses) the Google Identity Services client and resolves
// the google.accounts.id API. The ezAuth Google sign-in button uses it to render
// a "Sign in with Google" button and capture the id_token, which SM then hands
// to ezAuth's loginWithGoogleCredential. Any failure (script load, init timeout)
// rejects so the caller can hide the button and fall back to email/password.

// Google's hosted Identity Services bundle. Mirrors the same src staticsol's
// GoogleOneTap loads; the data-ez-gsi marker lets a second injector reuse it.
const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const GSI_SCRIPT_MARKER = 'data-ez-gsi';

// Upper bound on waiting for the injected bundle to publish google.accounts.id.
const GSI_READY_TIMEOUT_MS = 10000;
const GSI_POLL_INTERVAL_MS = 50;

// The credential delivered to an initialize() callback. credential is the
// Google Identity Services id_token (a JWT); SM never decodes or logs it, only
// forwards it to ezAuth for server-side verification.
export interface GsiCredentialResponse {
  credential: string;
  select_by?: string;
}

// Config passed to google.accounts.id.initialize. nonce binds the issued
// id_token to a server-started flow; ux_mode 'popup' keeps the flow off a
// full-page redirect so checkout state survives.
export interface GsiInitializeConfig {
  client_id: string;
  callback: (response: GsiCredentialResponse) => void;
  nonce?: string;
  ux_mode?: 'popup' | 'redirect';
  itp_support?: boolean;
}

// Options passed to google.accounts.id.renderButton.
export interface GsiButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

// The slice of the Google Identity Services API SM depends on, mirrored locally.
export interface GsiIdApi {
  initialize: (config: GsiInitializeConfig) => void;
  renderButton: (parent: HTMLElement, options?: GsiButtonOptions) => void;
  cancel: () => void;
}

interface GsiWindow extends Window {
  google?: { accounts?: { id?: GsiIdApi } };
}

// ensureGsi loads (or reuses) the Google Identity Services client and resolves
// its google.accounts.id API. Rejects on script load failure or init timeout.
export async function ensureGsi(): Promise<GsiIdApi> {
  const win = window as GsiWindow;

  const existing = win.google?.accounts?.id;
  if (existing) {
    return existing;
  }

  await injectGsiScript();

  const api = await waitForGsiApi(win);
  if (!api) {
    throw new Error('Google Identity Services did not initialize in time');
  }
  return api;
}

// injectGsiScript appends the GSI client once and resolves on load / rejects on
// error. A matching tag already in the DOM (publisher install, or a second
// injector) is reused.
function injectGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[${GSI_SCRIPT_MARKER}]`) || document.querySelector(`script[src="${GSI_SCRIPT_URL}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.setAttribute(GSI_SCRIPT_MARKER, '1');
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Google Identity Services script load failed')), { once: true });
    document.head.appendChild(script);
  });
}

// waitForGsiApi polls until window.google.accounts.id is published, or the
// timeout elapses (resolves null).
function waitForGsiApi(win: GsiWindow): Promise<GsiIdApi | null> {
  return new Promise((resolve) => {
    const deadline = Date.now() + GSI_READY_TIMEOUT_MS;
    const check = (): void => {
      const api = win.google?.accounts?.id;
      if (api) {
        resolve(api);
        return;
      }
      if (Date.now() >= deadline) {
        resolve(null);
        return;
      }
      setTimeout(check, GSI_POLL_INTERVAL_MS);
    };
    check();
  });
}
